import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { apiClient, EhubLogEntry } from '../../services/api';
import { useEnvironment } from '../../contexts/EnvironmentContext';
import { getApiBaseUrl } from '../../config';
import { useOrderById } from '../../hooks/queries';
import {
  formatOrderBackLabel,
  resolveOrderDetailBack,
} from '../../utils/order-navigation';

/**
 * Shared height for every card in the order detail grid. Sized so the Ordered
 * Items list — the only block whose content is variable — shows six rows plus
 * its header before scrolling. Every other block fits well inside it.
 */
const DETAIL_BLOCK_HEIGHT = '21rem';

export function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { environment } = useEnvironment();
  const [ehubLogsModalOpen, setEhubLogsModalOpen] = useState(false);
  const [ehubLogsLoading, setEhubLogsLoading] = useState(false);
  const [ehubLogsError, setEhubLogsError] = useState<string | null>(null);
  const [ehubLogsResults, setEhubLogsResults] = useState<EhubLogEntry[] | null>(null);
  const [ehubLogsExpandedRows, setEhubLogsExpandedRows] = useState<Set<number>>(new Set());
  const [ehubLogsPayloadExpandedRows, setEhubLogsPayloadExpandedRows] = useState<Set<number>>(new Set());
  const [ehubLogsFilterErrorsOnly, setEhubLogsFilterErrorsOnly] = useState(false);

  const { data: orderResponse, isLoading: loading, error: queryError } = useOrderById(parseInt(id ?? '0'));
  const order = orderResponse?.data ?? null;
  const error = queryError ? (queryError as any).message ?? 'Failed to fetch order' : null;
  const backNav = resolveOrderDetailBack(location.state);

  const formatDate = (dateString: string) => {
    if (!dateString) {
      return 'N/A';
    }
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getPaymentMethodDisplay = (order: any) => {
    if (order.payment_method) {
      return order.payment_method;
    }
    if (order.payment_provider_id) {
      const providers: Record<string, string> = {
        'authorizenet': 'Authorize.net',
        'stripe': 'Stripe',
        'paypal': 'PayPal',
        'square': 'Square',
        'manual': 'Manual',
        'adyen': 'Adyen',
      };
      return providers[order.payment_provider_id] || order.payment_provider_id;
    }
    return 'N/A';
  };

  const formatAddress = (address: any) => {
    if (!address) {
      return null;
    }
    const parts = [];
    if (address.street_1) {
      parts.push(address.street_1);
      if (address.street_2) {
        parts.push(address.street_2);
      }
    }
    const cityStateZip = [
      address.city,
      address.state,
      address.zip,
    ].filter(Boolean).join(' ');
    if (cityStateZip) {
      parts.push(cityStateZip);
    }
    if (address.country) {
      parts.push(address.country);
    }
    return parts.length > 0 ? parts : null;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      // Could add a toast notification here
    });
  };

  const isLocalMode = (getApiBaseUrl() ?? '').includes('localhost');
  const showEhubLogsLink =
    (environment === 'stage' || environment === 'prod') && isLocalMode;

  /** Extract lambda function name from full log group path (e.g. /aws/lambda/foo-function -> foo-function) */
  const getLambdaName = (logGroup: string): string => {
    if (!logGroup) {
      return '';
    }
    const parts = logGroup.split('/').filter(Boolean);
    return parts.length > 0 ? parts[parts.length - 1] : logGroup;
  };

  /** Unescape common escape sequences in a string for display */
  const unescapeString = (s: string): string => {
    if (!s || typeof s !== 'string') {
      return '';
    }
    return s
      .replace(/\\\\/g, '\\')
      .replace(/\\n/g, '\n')
      .replace(/\\t/g, '\t')
      .replace(/\\r/g, '\r')
      .replace(/\\"/g, '"');
  };

  /** Normalize a single payload value: parse JSON strings, strip escape chars */
  const normalizePayloadValue = (payload: unknown): unknown => {
    if (payload === null || payload === undefined) {
      return payload;
    }
    if (typeof payload === 'string') {
      const unescaped = unescapeString(payload);
      const trimmed = unescaped.trim();
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        try {
          return JSON.parse(trimmed);
        } catch {
          return unescaped;
        }
      }
      return unescaped;
    }
    return payload;
  };

  /** Recursively find and format any Payload/payload field in an object (e.g. Properties.Event.Payload). Parses string values that look like JSON so nested Payload is found. */
  const normalizePayloadInObject = (obj: unknown): unknown => {
    if (obj === null || obj === undefined) {
      return obj;
    }
    if (Array.isArray(obj)) {
      return obj.map(normalizePayloadInObject);
    }
    if (typeof obj === 'string') {
      const unescaped = unescapeString(obj);
      const trimmed = unescaped.trim();
      if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
        try {
          return normalizePayloadInObject(JSON.parse(trimmed));
        } catch {
          return unescaped;
        }
      }
      return unescaped;
    }
    if (typeof obj === 'object') {
      const record = obj as Record<string, unknown>;
      const result: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(record)) {
        const lower = key.toLowerCase();
        if (lower === 'payload') {
          result[key] = normalizePayloadValue(value);
        } else {
          result[key] = normalizePayloadInObject(value);
        }
      }
      return result;
    }
    return obj;
  };

  /** Format log message for display: pretty-print JSON; format any Payload field (nested or top-level) and strip escape chars */
  const formatLogMessage = (msg: string): string => {
    if (!msg || typeof msg !== 'string') {
      return '';
    }
    const trimmed = msg.trim();
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        const parsed = JSON.parse(trimmed);
        const normalized = normalizePayloadInObject(parsed);
        return JSON.stringify(normalized, null, 2);
      } catch {
        return unescapeString(msg);
      }
    }
    return unescapeString(msg);
  };

  /** Recursively find first Payload/payload value in an object; returns undefined if not found */
  const findPayloadInObject = (obj: unknown): unknown => {
    if (obj === null || obj === undefined) {
      return undefined;
    }
    if (Array.isArray(obj)) {
      for (const item of obj) {
        const found = findPayloadInObject(item);
        if (found !== undefined) {
          return found;
        }
      }
      return undefined;
    }
    if (typeof obj === 'object') {
      const record = obj as Record<string, unknown>;
      for (const [key, value] of Object.entries(record)) {
        if (key.toLowerCase() === 'payload') {
          return value;
        }
        const found = findPayloadInObject(value);
        if (found !== undefined) {
          return found;
        }
      }
    }
    return undefined;
  };

  /** Get formatted payload string from message for display/copy, or null if no payload */
  const getPayloadDisplayString = (msg: string): string | null => {
    if (!msg || typeof msg !== 'string') {
      return null;
    }
    const trimmed = msg.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
      return null;
    }
    try {
      const parsed = JSON.parse(trimmed);
      const normalized = normalizePayloadInObject(parsed);
      const payload = findPayloadInObject(normalized);
      if (payload === undefined) {
        return null;
      }
      if (typeof payload === 'object') {
        return JSON.stringify(payload, null, 2);
      }
      return String(payload);
    } catch {
      return null;
    }
  };

  const toggleEhubLogsPayloadRow = (idx: number) => {
    setEhubLogsPayloadExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const COLLAPSIBLE_PREVIEW_LEN = 120;
  const toggleEhubLogsRow = (idx: number) => {
    setEhubLogsExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const openEhubLogsModal = async () => {
    if (!order?.id || !order?.date_created) {
      return;
    }
    setEhubLogsModalOpen(true);
    setEhubLogsLoading(true);
    setEhubLogsError(null);
    setEhubLogsResults(null);
    setEhubLogsExpandedRows(new Set());
    setEhubLogsPayloadExpandedRows(new Set());
    try {
      const response = await apiClient.getOrderEhubLogs(
        order.id,
        order.date_created
      );
      if (response.success && response.data?.results) {
        setEhubLogsResults(response.data.results);
      } else {
        setEhubLogsError(response.message || 'No log data returned.');
      }
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: string }).message) : 'Failed to load eHub logs.';
      setEhubLogsError(msg);
    } finally {
      setEhubLogsLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading order details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '2rem' }}>
        <div
          style={{
            backgroundColor: '#fee',
            border: '1px solid #fcc',
            borderRadius: '4px',
            padding: '1rem',
            color: '#c33',
            marginBottom: '1rem',
          }}
        >
          <strong>Error:</strong> {error}
        </div>
        <button
          onClick={() => navigate(backNav.path)}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#3498db',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          {formatOrderBackLabel(backNav.label)}
        </button>
      </div>
    );
  }

  if (!order) {
    return null;
  }

  const customerName = order.billing_address
    ? `${order.billing_address.first_name || ''} ${order.billing_address.last_name || ''}`.trim()
    : order.customer
    ? `${order.customer.first_name || ''} ${order.customer.last_name || ''}`.trim()
    : 'N/A';

  const customerEmail = order.billing_address?.email || order.customer?.email || 'N/A';
  const customerPhone = order.billing_address?.phone || order.customer?.phone || 'N/A';

  const ipAddress = order.customer_ip_address || order.ip_address || 'N/A';
  const _orderSource = order.order_source || order.source || 'N/A';

  const paymentStatus = order.payment_status || order.status || 'N/A';
  const transactionId = order.payment_transaction_id || order.transaction_id || order.external_id || 'N/A';
  const pspReference =
    (typeof order.psp_reference === 'string' && order.psp_reference.trim()) ||
    undefined;
  const cartId =
    (typeof order.cart_id === 'string' && order.cart_id.trim()) ||
    undefined;

  const shippingMethod = order.shipping_method ||
    (order.shipping_methods && order.shipping_methods[0]?.method) ||
    (order.shipping_methods && order.shipping_methods[0]?.description) ||
    'N/A';
  const _shippingLocation = order.shipping_methods?.[0]?.location || 'Default location';
  const shippingRegion = order.shipping_region ||
    (order.shipping_addresses?.[0]?.country) ||
    'N/A';

  const discountAmount = order.discount_amount || order.coupon_discount || order.discount_total || 0;

  // Calculate subtotal from products if not provided by API
  const calculateSubtotalFromProducts = () => {
    const products = order.products || order.product_line_items || [];
    if (products.length === 0) {
      return 0;
    }

    return products.reduce((sum: number, product: any) => {
      const quantity = parseFloat(product.quantity || product.quantity_ordered || '0');
      // Prioritize price_ex_tax (excluding tax) over price_inc_tax (including tax)
      const price = parseFloat(product.base_price || product.price_ex_tax || product.price_inc_tax || '0');
      return sum + (price * quantity);
    }, 0);
  };

  const subtotal = parseFloat(order.subtotal || order.subtotal_ex_tax || '0') || calculateSubtotalFromProducts();

  const billingCountryIso2 = order.billing_address?.country_iso2 || order.geoip_country_iso2 || '';
  const billingCountryName = order.billing_address?.country || order.geoip_country || '';
  const itemCount =
    Number(order.items_total) ||
    (order.products || order.product_line_items || []).length ||
    0;
  const flagEmoji = billingCountryIso2.length === 2
    ? String.fromCodePoint(...billingCountryIso2.toUpperCase().split('').map((c: string) => 0x1F1E0 + c.charCodeAt(0) - 65))
    : '🌍';

  const paymentIcon = (() => {
    const method = (order.payment_method || order.payment_provider_id || '').toLowerCase();
    if (method.includes('visa'))       { return { label: 'VISA',   bg: '#1a1f71', text: '#fff' }; }
    if (method.includes('mastercard')) { return { label: 'MC',     bg: '#eb001b', text: '#fff' }; }
    if (method.includes('amex'))       { return { label: 'AMEX',   bg: '#007bc1', text: '#fff' }; }
    if (method.includes('paypal'))     { return { label: 'PayPal', bg: '#003087', text: '#fff' }; }
    if (method.includes('stripe'))     { return { label: 'Stripe', bg: '#635bff', text: '#fff' }; }
    if (method.includes('apple'))      { return { label: '🍎 Pay', bg: '#000',   text: '#fff' }; }
    if (method.includes('google'))     { return { label: 'G Pay',  bg: '#4285f4', text: '#fff' }; }
    return { label: '💳', bg: '#475569', text: '#fff' };
  })();

  const statusClass = (() => {
    const s = (order.status || '').toLowerCase();
    if (['completed','shipped','fulfilled'].some(k => s.includes(k))) { return 'badge-success'; }
    if (['pending','awaiting','processing'].some(k => s.includes(k))) { return 'badge-warning'; }
    if (['cancelled','canceled','declined','refunded'].some(k => s.includes(k))) { return 'badge-error'; }
    return 'badge-neutral';
  })();

  return (
    <div className="page-container">
      {/* Header */}
      <button className="btn-back" onClick={() => navigate(backNav.path)}>
        {formatOrderBackLabel(backNav.label)}
      </button>

      <div
        className="order-detail-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
          gap: '0.75rem',
        }}
      >
        <div
          className="order-detail-header-meta"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.65rem',
            flexWrap: 'wrap',
            fontSize: '0.9375rem',
            fontWeight: 700,
            color: 'var(--text-primary)',
          }}
        >
          <h1 className="page-title" style={{ margin: 0 }}>
            Order #{order.id}
          </h1>
          <span aria-hidden="true">|</span>
          <span className={`badge ${statusClass}`}>{order.status}</span>
          {billingCountryName ? (
            <>
              <span aria-hidden="true">|</span>
              <span>{billingCountryName}</span>
            </>
          ) : null}
          <span aria-hidden="true">|</span>
          <span>{formatCurrency(parseFloat(order.total_inc_tax || order.total || '0'))}</span>
          <span aria-hidden="true">|</span>
          <span>{formatDate(order.date_created)}</span>
          <span aria-hidden="true">|</span>
          <span>
            {itemCount} item{itemCount === 1 ? '' : 's'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {showEhubLogsLink && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={openEhubLogsModal}>
              eHub Logs
            </button>
          )}
        </div>
      </div>

      {/* eHub logs modal */}
      {ehubLogsModalOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setEhubLogsModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ehub-logs-title"
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '8px',
              width: '95vw',
              maxWidth: '1400px',
              height: '90vh',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '0.5rem 1rem', borderBottom: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 id="ehub-logs-title" style={{ margin: 0, fontSize: '1rem' }}>eHub logs for Order #{order.id}</h2>
              <button
                type="button"
                onClick={() => setEhubLogsModalOpen(false)}
                style={{
                  padding: '0.2rem 0.45rem',
                  fontSize: '0.8rem',
                  background: '#f0f0f0',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
            <p style={{ margin: 0, padding: '0.35rem 1rem', fontSize: '0.75rem', color: '#666', backgroundColor: '#f9f9f9' }}>
              For local debugging; requires AWS CLI sign-in. Time range: order creation date through +3 days.
            </p>
            {!ehubLogsLoading && !ehubLogsError && ehubLogsResults !== null && ehubLogsResults.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.35rem 1rem',
                  borderBottom: '1px solid #eee',
                  backgroundColor: '#f5f5f5',
                  fontSize: '0.75rem',
                }}
              >
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={ehubLogsFilterErrorsOnly}
                    onChange={(e) => {
                      setEhubLogsFilterErrorsOnly(e.target.checked);
                    }}
                  />
                  Show errors only
                </label>
                {ehubLogsFilterErrorsOnly && (
                  <span style={{ color: '#666' }}>
                    Showing {ehubLogsResults.filter((e) => e.isError).length} of {ehubLogsResults.length} entries
                  </span>
                )}
              </div>
            )}
            <div style={{ padding: '0.5rem 1rem', overflow: 'auto', flex: 1, minHeight: 0, fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.4 }}>
              {ehubLogsLoading && <p style={{ margin: 0 }}>Loading logs…</p>}
              {ehubLogsError && (
                <p style={{ margin: 0, color: '#c00', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.75rem' }}>{ehubLogsError}</p>
              )}
              {!ehubLogsLoading && !ehubLogsError && ehubLogsResults !== null && (
                <>
                  {ehubLogsResults.length === 0 ? (
                    <p style={{ margin: 0, color: '#666' }}>No log entries found for this order in the time range.</p>
                  ) : (() => {
                    const filteredWithIndex = ehubLogsResults
                      .map((entry, idx) => ({ entry, idx }))
                      .filter(({ entry }) => !ehubLogsFilterErrorsOnly || entry.isError);
                    if (filteredWithIndex.length === 0) {
                      return (
                        <p style={{ margin: 0, color: '#666' }}>
                          No error entries. Uncheck &quot;Show errors only&quot; to see all logs.
                        </p>
                      );
                    }
                    return (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #ddd', textAlign: 'left' }}>
                          <th style={{ padding: '0.35rem 0.5rem', width: '130px' }}>Time</th>
                          <th style={{ padding: '0.35rem 0.5rem', minWidth: '140px' }}>Log group</th>
                          <th style={{ padding: '0.35rem 0.5rem', width: '56px' }}>Level</th>
                          <th style={{ padding: '0.35rem 0.5rem' }}>Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredWithIndex.map(({ entry, idx }) => {
                          const expanded = ehubLogsExpandedRows.has(idx);
                          const rendered = formatLogMessage(entry.message);
                          const raw = entry.message ?? '';
                          const short = raw.length <= COLLAPSIBLE_PREVIEW_LEN ? raw : raw.slice(0, COLLAPSIBLE_PREVIEW_LEN) + '…';
                          const showToggle = raw.length > COLLAPSIBLE_PREVIEW_LEN;
                          const errorRowStyle = entry.isError
                            ? { backgroundColor: '#ffebee' as const, borderLeft: '3px solid #c62828' }
                            : {};
                          return (
                            <React.Fragment key={idx}>
                              <tr
                                style={{
                                  borderBottom: expanded ? 'none' : '1px solid #eee',
                                  ...errorRowStyle,
                                }}
                              >
                                <td style={{ padding: '0.35rem 0.5rem', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>{entry.timestamp}</td>
                                <td style={{ padding: '0.35rem 0.5rem', verticalAlign: 'middle', wordBreak: 'break-all' }}>
                                  {getLambdaName(entry.logGroup)}
                                </td>
                                <td
                                  style={{
                                    padding: '0.35rem 0.5rem',
                                    verticalAlign: 'middle',
                                    fontWeight: entry.isError ? 600 : undefined,
                                    color: entry.isError ? '#b71c1c' : undefined,
                                  }}
                                >
                                  {entry.isError ? 'ERROR' : 'INFO'}
                                </td>
                                <td style={{ padding: '0.35rem 0.5rem', verticalAlign: 'middle' }}>
                                  {!expanded && short}
                                  {showToggle && (
                                    <button
                                      type="button"
                                      onClick={() => toggleEhubLogsRow(idx)}
                                      style={{
                                        marginLeft: '0.25rem',
                                        padding: '0.1rem 0.35rem',
                                        fontSize: '0.7rem',
                                        background: 'none',
                                        border: 'none',
                                        color: '#3498db',
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                      }}
                                    >
                                      {expanded ? 'Show less' : 'Show more'}
                                    </button>
                                  )}
                                </td>
                              </tr>
                              {expanded && (
                                <tr
                                  style={{
                                    borderBottom: '1px solid #eee',
                                    backgroundColor: entry.isError ? '#ffebee' : '#fafafa',
                                    borderLeft: entry.isError ? '3px solid #c62828' : undefined,
                                  }}
                                >
                                  <td colSpan={4} style={{ padding: '0.35rem 0.5rem', verticalAlign: 'top' }}>
                                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', width: '100%' }}>
                                      {(() => {
                                        const payloadStr = getPayloadDisplayString(entry.message);
                                        if (!payloadStr) {
                                          return null;
                                        }
                                        const payloadExpanded = ehubLogsPayloadExpandedRows.has(idx);
                                        return (
                                          <div
                                            style={{
                                              marginBottom: '0.5rem',
                                              border: '1px solid #ccc',
                                              borderRadius: '4px',
                                              overflow: 'hidden',
                                              backgroundColor: '#fff',
                                            }}
                                          >
                                            <div
                                              style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                                padding: '0.35rem 0.5rem',
                                                backgroundColor: '#e8e8e8',
                                                borderBottom: payloadExpanded ? '1px solid #ccc' : 'none',
                                              }}
                                            >
                                              <button
                                                type="button"
                                                onClick={() => toggleEhubLogsPayloadRow(idx)}
                                                style={{
                                                  padding: '0.2rem 0.35rem',
                                                  background: 'none',
                                                  border: 'none',
                                                  cursor: 'pointer',
                                                  fontSize: '0.75rem',
                                                  fontWeight: 600,
                                                  color: '#333',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: '0.25rem',
                                                }}
                                                aria-expanded={payloadExpanded}
                                                title={payloadExpanded ? 'Collapse payload' : 'Expand payload'}
                                              >
                                                <span style={{ fontSize: '0.65rem' }}>{payloadExpanded ? '▼' : '▶'}</span>
                                                Payload
                                              </button>
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  navigator.clipboard.writeText(payloadStr).catch(() => {});
                                                }}
                                                style={{
                                                  padding: '0.2rem 0.5rem',
                                                  fontSize: '0.7rem',
                                                  background: '#fff',
                                                  border: '1px solid #999',
                                                  borderRadius: '3px',
                                                  cursor: 'pointer',
                                                  color: '#333',
                                                }}
                                                title="Copy payload"
                                              >
                                                Copy
                                              </button>
                                            </div>
                                            {payloadExpanded && (
                                              <div style={{ padding: '0.5rem', maxHeight: '300px', overflow: 'auto', fontSize: '0.72rem' }}>
                                                {payloadStr}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })()}
                                      <div style={{ marginBottom: '0.5rem' }}>
                                        <strong style={{ fontSize: '0.7rem', color: '#666' }}>Rendered message:</strong>
                                        <div style={{ marginTop: '0.2rem' }}>{rendered}</div>
                                      </div>
                                      <div>
                                        <strong style={{ fontSize: '0.7rem', color: '#666' }}>Message:</strong>
                                        <div style={{ marginTop: '0.2rem' }}>{unescapeString(raw)}</div>
                                      </div>
                                      {showToggle && (
                                        <button
                                          type="button"
                                          onClick={() => toggleEhubLogsRow(idx)}
                                          style={{
                                            marginTop: '0.25rem',
                                            padding: '0.1rem 0.35rem',
                                            fontSize: '0.7rem',
                                            background: 'none',
                                            border: 'none',
                                            color: '#3498db',
                                            cursor: 'pointer',
                                            textDecoration: 'underline',
                                          }}
                                        >
                                          Show less
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main layout: left info + right items */}
      <div className="detail-layout" style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: '1.25rem', alignItems: 'start' }}>

        {/* LEFT: Customer, Payment, Shipping */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

          {/* Customer */}
          <div className="card card-padded" style={{ display: 'flex', flexDirection: 'column', height: DETAIL_BLOCK_HEIGHT, overflow: 'hidden' }}>
            <p className="section-label">Customer</p>
            <p style={{ margin: '0 0 0.25rem', fontWeight: 700, color: 'var(--text-primary)', fontSize: '1rem' }}>{customerName}</p>
            <p style={{ margin: '0 0 0.1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{customerEmail}</p>
            {customerPhone !== 'N/A' && <p style={{ margin: '0 0 0.1rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{customerPhone}</p>}
            {order.billing_address && (
              <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--color-border)', fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                <span style={{ marginRight: '0.4rem' }}>{flagEmoji}</span>
                {formatAddress(order.billing_address)?.join(', ')}
              </div>
            )}
            <div style={{ marginTop: 'auto', paddingTop: '0.75rem', borderTop: '1px solid var(--color-border)', display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.8rem', flexShrink: 0 }}>
              <div><p className="info-label">Ordered</p><p className="info-value-muted">{formatDate(order.date_created)}</p></div>
              {ipAddress !== 'N/A' && <div><p className="info-label">IP</p><p className="info-value-muted" style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem' }}>{ipAddress}</p></div>}
            </div>
          </div>

          {/* Payment */}
          <div className="card card-padded" style={{ display: 'flex', flexDirection: 'column', height: DETAIL_BLOCK_HEIGHT, overflow: 'hidden' }}>
            <p className="section-label">Payment</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <span style={{ background: paymentIcon.bg, color: paymentIcon.text, padding: '0.2rem 0.6rem', borderRadius: 4, fontWeight: 800, fontSize: '0.75rem', letterSpacing: '0.04em', fontFamily: 'var(--font-mono)', flexShrink: 0 }}>
                {paymentIcon.label}
              </span>
              <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.875rem' }}>{getPaymentMethodDisplay(order)}</span>
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.55rem',
                fontSize: '0.8rem',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                }}
              >
                <p className="info-label" style={{ margin: 0 }}>
                  Status
                </p>
                <span
                  className={`badge ${
                    ['captured', 'paid'].includes(paymentStatus.toLowerCase())
                      ? 'badge-success'
                      : 'badge-warning'
                  }`}
                >
                  {paymentStatus}
                </span>
              </div>
              {transactionId !== 'N/A' && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: '0.75rem',
                  }}
                >
                  <p className="info-label" style={{ margin: 0, flexShrink: 0 }}>
                    Transaction
                  </p>
                  <p
                    className="info-value-muted"
                    style={{
                      margin: 0,
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.75rem',
                      textAlign: 'right',
                      wordBreak: 'break-all',
                    }}
                  >
                    {transactionId}
                  </p>
                </div>
              )}
              {pspReference && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: '0.75rem',
                    minWidth: 0,
                  }}
                >
                  <p className="info-label" style={{ margin: 0, flexShrink: 0 }}>
                    PSP Reference
                  </p>
                  <p
                    className="info-value-muted"
                    title={pspReference}
                    style={{
                      margin: 0,
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.75rem',
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                      overflowX: 'auto',
                      minWidth: 0,
                      maxWidth: '100%',
                    }}
                  >
                    {pspReference}
                  </p>
                </div>
              )}
              {cartId && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: '0.75rem',
                    minWidth: 0,
                  }}
                >
                  <p className="info-label" style={{ margin: 0, flexShrink: 0 }}>
                    Cart ID
                  </p>
                  <p
                    className="info-value-muted"
                    title={cartId}
                    style={{
                      margin: 0,
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.75rem',
                      textAlign: 'right',
                      whiteSpace: 'nowrap',
                      overflowX: 'auto',
                      minWidth: 0,
                      maxWidth: '100%',
                    }}
                  >
                    {cartId}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Shipping */}
          {order.shipping_addresses && order.shipping_addresses.length > 0 && (
            <div className="card card-padded" style={{ display: 'flex', flexDirection: 'column', height: DETAIL_BLOCK_HEIGHT, overflow: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexShrink: 0 }}>
                <p className="section-label" style={{ margin: 0 }}>Shipping</p>
                <button className="btn btn-ghost btn-sm" onClick={() => { const addr = order.shipping_addresses[0]; copyToClipboard(formatAddress(addr)?.join('\n') || ''); }}>Copy address</button>
              </div>
              {order.shipping_addresses.map((addr: any, idx: number) => {

                return (
                  <div key={idx} style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', lineHeight: 1.7 }}>
                    <strong style={{ color: 'var(--text-primary)' }}>{addr.first_name} {addr.last_name}</strong><br />
                    {formatAddress(addr)?.join(', ')}
                  </div>
                );
              })}
              <div style={{ marginTop: 'auto', paddingTop: '0.75rem', borderTop: '1px solid var(--color-border)', display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.8rem', flexShrink: 0 }}>
                <div><p className="info-label">Method</p><p className="info-value-muted">{shippingMethod}</p></div>
                <div><p className="info-label">Cost</p><p className="info-value-muted">{formatCurrency(parseFloat(order.shipping_cost_inc_tax || order.shipping_cost || '0'))}</p></div>
                {shippingRegion !== 'N/A' && <div><p className="info-label">Region</p><p className="info-value-muted">{shippingRegion}</p></div>}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: Products + Summary */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

          {/* Items */}
          <div className="card" style={{ display: 'flex', flexDirection: 'column', height: DETAIL_BLOCK_HEIGHT, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', padding: '1.5rem 1.5rem 0.75rem', flexShrink: 0 }}>
              <p className="section-label" style={{ margin: 0 }}>Ordered Items</p>
              <span className="badge badge-info">{(order.products || order.product_line_items || []).length} item{(order.products || order.product_line_items || []).length !== 1 ? 's' : ''}</span>
            </div>
            <div className="card-body" style={{ padding: 0, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
              {(order.products || order.product_line_items || []).length === 0 ? (
                <div className="empty-state" style={{ padding: '2rem' }}>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: 0 }}>No product items available.</p>
                </div>
              ) : (
                (order.products || order.product_line_items || []).map((product: any, idx: number) => (
                  <div key={idx} style={{ padding: '0.75rem 1.5rem', borderBottom: idx < (order.products || order.product_line_items || []).length - 1 ? '1px solid var(--color-border)' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'nowrap', minWidth: 0 }}>
                        <span style={{ background: 'var(--color-bg)', border: '1px solid var(--color-border)', borderRadius: 4, padding: '0.1rem 0.4rem', fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', flexShrink: 0 }}>
                          ×{product.quantity || product.quantity_ordered || 0}
                        </span>
                        <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.875rem', lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{product.name || product.product_name || 'N/A'}</span>
                        {(product.sku || product.product_sku) && (
                          <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--text-muted)', background: 'var(--color-bg)', padding: '0.1rem 0.4rem', borderRadius: 4, border: '1px solid var(--color-border)', flexShrink: 0 }}>
                            {product.sku || product.product_sku}
                          </span>
                        )}
                      </div>
                      {product.custom_fields && product.custom_fields.length > 0 && (
                        <div style={{ marginTop: '0.4rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                          {product.custom_fields.map((f: any, fi: number) => (
                            <span key={fi} style={{ marginRight: '0.75rem' }}><strong>{f.name || f.display_name}:</strong> {f.value}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <span className="amount" style={{ fontSize: '0.9rem' }}>{formatCurrency(parseFloat(product.base_price || product.price_ex_tax || product.price_inc_tax || '0'))}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Applied Promotions & Coupons */}
          {Array.isArray(order.applied_discounts) && order.applied_discounts.length > 0 && (
            <div className="card card-padded" style={{ display: 'flex', flexDirection: 'column', height: DETAIL_BLOCK_HEIGHT, overflow: 'hidden' }}>
              <p className="section-label" style={{ flexShrink: 0 }}>Applied Promotions &amp; Coupons</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {order.applied_discounts.map((discount: {
                  id?: number;
                  promotion_id?: number;
                  coupon_id?: number;
                  linkable?: boolean;
                  name?: string;
                  code?: string;
                  type?: string;
                  amount?: string | number;
                }, index: number) => {
                  const promoId = discount.coupon_id ?? discount.promotion_id;
                  const isLinkable = discount.linkable === true && promoId !== undefined;
                  const label = discount.name || discount.code || `Discount ${index + 1}`;
                  const amount = parseFloat(String(discount.amount ?? '0'));
                  return (
                    <div
                      key={`${label}-${index}`}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.75rem',
                        background: 'var(--color-bg)',
                        borderRadius: 8,
                        border: '1px solid var(--color-border)',
                      }}
                    >
                      <div>
                        {isLinkable ? (
                          <button
                            type="button"
                            className="btn-link"
                            onClick={() => navigate(`/promotions/${promoId}`)}
                            style={{ fontWeight: 600, padding: 0, border: 'none', background: 'none', cursor: 'pointer', color: 'var(--color-primary)' }}
                          >
                            {label}
                          </button>
                        ) : (
                          <span style={{ fontWeight: 600 }}>{label}</span>
                        )}
                        {discount.code && discount.code !== label && (
                          <span style={{ marginLeft: '0.5rem', fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                            {discount.code}
                          </span>
                        )}
                        {discount.type && (
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>{discount.type}</div>
                        )}
                      </div>
                      {amount > 0 && (
                        <span style={{ fontWeight: 600, color: 'var(--color-success)' }}>
                          −{formatCurrency(amount)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="card card-padded" style={{ display: 'flex', flexDirection: 'column', height: DETAIL_BLOCK_HEIGHT, overflow: 'hidden' }}>
            <p className="section-label" style={{ flexShrink: 0 }}>Order Summary</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', fontSize: '0.875rem', flex: '1 1 auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-secondary)' }}>
                <span>Subtotal</span><span>{formatCurrency(subtotal)}</span>
              </div>
              {discountAmount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--color-success)' }}>
                  <span>Discount</span><span>−{formatCurrency(parseFloat(String(discountAmount)))}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-secondary)' }}>
                <span>Shipping</span><span>{formatCurrency(parseFloat(order.shipping_cost_inc_tax || order.shipping_cost || '0'))}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--text-secondary)' }}>
                <span>Tax</span><span>{formatCurrency(parseFloat(order.total_tax || '0'))}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.75rem', marginTop: 'auto', borderTop: '2px solid var(--color-border)', fontWeight: 800, fontSize: '1.1rem', color: 'var(--text-primary)' }}>
                <span>Total</span>
                <span className="amount" style={{ fontSize: '1.1rem' }}>{formatCurrency(parseFloat(order.total_inc_tax || order.total || '0'))}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
